import { useEffect, useRef } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/core';
import { dropdownColors } from '../../../app/colors';
import { gutters, layers } from '../../../app/spacing';
import Icon from '../../Kizen/Icon';
import { TextEllipsisWithTooltip } from '../../Kizen/Table';
import { blue, gray, red } from '@kizen/kds/Colors';

const Label = styled.label`
  display: inline-flex;
  align-items: center;
  max-width: 100%;
  margin-bottom: unset; // Undo global label style
  // These two ensure that the height of the parent doesn't
  // change when the box is checked versus unchecked. Just an inline sizing quirk.
  line-height: 0;
  vertical-align: top;
  > input {
    position: absolute;
    opacity: 0;
    z-index: ${layers.content(0)};
    & + * {
      flex-shrink: 0;
      .CheckboxCheckIcon,
      .CheckboxIndeterminateIcon {
        display: none;
      }
    }
    &:checked + * {
      border-color: ${blue['blue-700']};
      background-color: ${blue['blue-700']};
      .CheckboxCheckIcon {
        display: initial;
      }
      .CheckboxIndeterminateIcon {
        display: none;
      }
    }
    &:indeterminate + * {
      border-color: ${blue['blue-700']};
      background-color: ${blue['blue-700']};
      .CheckboxCheckIcon {
        display: none;
      }
      .CheckboxIndeterminateIcon {
        display: initial;
      }
    }
    &:disabled + * {
      border-color: ${gray['gray-600']};
      background-color: ${gray['gray-50']};
      svg {
        color: ${gray['gray-600']};
      }
    }

    &:disabled:checked + * {
      border-color: ${gray['gray-600']};
      background-color: ${gray['gray-600']};
      svg {
        color: ${gray['gray-50']};
      }
    }
    &:disabled + * + * {
      color: ${gray['gray-600']};
    }
    &:focus-visible + * {
      border-color: ${blue['blue-700']};
      background: ${gray['gray-50']};
      svg {
        color: ${blue['blue-700']};
      }
    }
    &:focus-visible:checked + *,
    &:indeterminate:checked + * {
      background-color: ${dropdownColors.hover};
    }
  }
  &:hover > input:not(:disabled) + *,
  &:focus > input:not(:disabled) + * {
    border-color: ${blue['blue-700']};
    background: ${gray['gray-50']};
    svg {
      color: ${blue['blue-700']};
    }
  }
  &:hover > input:checked:not(:disabled) + *,
  &:focus-visible > input:checked:not(:disabled) + *,
  &:indeterminate > input:checked:not(:disabled) + * {
    background-color: ${dropdownColors.hover};
  }
  ${({ error }) =>
    error &&
    css`
      > input {
        & + *,
        &:checked + *,
        &:indeterminate + * {
          border-color: ${red['red-500']};
        }
      }
      &:hover > input:not(:disabled) + * {
        border-color: ${red['red-500']};
      }
    `}
`;

const VisualCheckbox = styled.span`
  box-sizing: content-box;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${gray['gray-600']};
  background-color: ${gray['gray-50']};
  border-radius: 2px;
  &:not(:last-child) {
    margin-right: ${gutters.spacing(2)}px;
  }
  width: 15px;
  height: 15px;
  && svg {
    color: ${gray['gray-50']};
    height: auto;
  }
  && .CheckboxCheckIcon svg {
    width: 13px;
  }
  && .CheckboxIndeterminateIcon svg {
    width: 11px;
  }
  ${({ variant }) =>
    variant === 'underline' &&
    css`
      width: 12px;
      height: 12px;
      && .CheckboxCheckIcon svg {
        width: 10px;
      }
      && .CheckboxIndeterminateIcon svg {
        width: 8px;
      }
    `}
`;

export default function Checkbox({
  label,
  variant,
  error,
  indeterminate,
  onChange,
  className,
  ...props
}) {
  const inputRef = useRef();
  useEffect(() => {
    if (inputRef.current) inputRef.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <Label className={className} error={error}>
      <input
        ref={inputRef}
        type="checkbox"
        onChange={(ev) => {
          if (onChange) onChange(ev.target.checked, ev);
        }}
        {...props}
      />
      <VisualCheckbox variant={variant}>
        <Icon className="CheckboxCheckIcon" icon="check" />
        <Icon className="CheckboxIndeterminateIcon" icon="minus" />
      </VisualCheckbox>
      {typeof label !== 'string' && label}
      {typeof label === 'string' && (
        <TextEllipsisWithTooltip
          as="span"
          style={{ height: 'calc(1em + 2px)' }}
        >
          {label}
        </TextEllipsisWithTooltip>
      )}
    </Label>
  );
}
